import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Inbox } from 'lucide-react';
import clsx from 'clsx';
import { EmptyState } from '@/components/ui';

interface Column<T> {
  key: keyof T | string;
  header: string;
  render?: (row: T) => ReactNode;
  className?: string;
}

interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  title?: string;
  emptyMessage?: string;
  maxRows?: number;
  /** Stable row key; falls back to the row index. */
  rowKey?: (row: T, index: number) => string;
  className?: string;
}

export function DataTable<T extends object>({
  data,
  columns,
  title,
  emptyMessage,
  maxRows,
  rowKey,
  className,
}: DataTableProps<T>) {
  const { t } = useTranslation();
  const displayData = maxRows ? data.slice(0, maxRows) : data;

  return (
    <div className={clsx('card min-w-0 overflow-hidden p-0', className)}>
      {title && (
        <div className="border-b border-gray-200 px-5 py-4">
          <h3 className="card-title">{title}</h3>
        </div>
      )}
      {displayData.length === 0 ? (
        <EmptyState icon={Inbox} title={emptyMessage ?? t('charts.noData')} />
      ) : (
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                {columns.map((col) => (
                  <th key={String(col.key)} scope="col" className={col.className}>
                    {col.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayData.map((row, rowIndex) => (
                <tr key={rowKey ? rowKey(row, rowIndex) : rowIndex}>
                  {columns.map((col) => (
                    <td key={String(col.key)} className={clsx('whitespace-nowrap', col.className)}>
                      {col.render ? col.render(row) : String(row[col.key as keyof T] ?? '—')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {maxRows && data.length > maxRows && (
        <div className="border-t border-gray-100 bg-gray-50 px-5 py-2.5 text-center text-xs text-gray-500">
          {t('charts.showingRows', { shown: maxRows, total: data.length })}
        </div>
      )}
    </div>
  );
}
